import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loading,
  TableControl,
  TabPaneControl,
} from 'spark-core-dx/components';
import { useCoreContext, useCoreStateContext } from 'spark-core-dx/contexts';
import { ItemManager } from 'spark-core-dx/managers';
import { ItemService } from 'spark-core-dx/services';
import ConvokeCommandBar from '../../components/convoke/ConvokeCommandBar';
import ResourceStateControl from '../resource/components/ResourceStateControl';
import {
  buildResourceWorkspacePath,
  RESOURCES_BASE_PATH,
} from '../resource/resourceRoutes';

const RESOURCE_ITEMS = Object.freeze({
  offers: 'ClientProductOffer',
  resources: 'ClientResource',
  configurations: 'ResourceConfiguration',
  setupRuns: 'ResourceConfigurationSetupRun',
  readiness: 'ResourceConfigurationReadinessSnapshot',
  capacity: 'RuntimeCapacityLease',
  usage: 'ResourceUsageEstimate',
});

const firstResult = (result) => (
  result?.first instanceof Function ? result.first() : result
);

const resultMessage = (result, fallback) => (
  result?.Message?.DisplayMessage
  || result?.MessageDetails?.[0]
  || fallback
);

const createScopedManager = ({
  itemName,
  clientId,
  currentEnvironment,
  coreContext,
  coreState,
}) => {
  const manager = new ItemManager(itemName, coreContext, coreState, true);
  const authoritativeService = manager._itemService;
  const service = new ItemService(
    `Command/Clients/${clientId}/Resources/Items/${itemName}`,
    coreState,
    coreContext,
    'PLATFORMAPP',
  );
  service._clientId = clientId;
  manager._itemService = service;
  const runAuthoritativeOperation = manager.RunOperation.bind(manager);
  manager.RunOperation = (options = {}) => runAuthoritativeOperation({
    ...options,
    itemServiceOverride: authoritativeService,
    item: options.item ? {
      ...options.item,
      ClientId: clientId,
      EnvironmentKey: currentEnvironment,
      CurrentEnvironment: currentEnvironment,
    } : options.item,
    queryParams: {
      ...(options.queryParams || {}),
      clientId,
      currentEnvironment,
    },
  });
  return manager;
};

const ResourceGrid = ({
  itemName,
  clientId,
  currentEnvironment,
  onItems,
  rowActions,
}) => {
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const manager = useMemo(() => createScopedManager({
    itemName,
    clientId,
    currentEnvironment,
    coreContext,
    coreState,
  }), [
    clientId,
    coreContext,
    coreState,
    currentEnvironment,
    itemName,
  ]);
  const managerRef = useRef(manager);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  managerRef.current = manager;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [metadataResult, queryResult] = await Promise.all([
        managerRef.current.GetMetadata({
          clientId,
          forceReload: true,
        }),
        managerRef.current.Query({
          model: null,
          skip: 0,
          take: 100,
          sortBy: 'UpdatedDate',
          sortOrder: 'DESC',
        }),
      ]);
      const metadata = firstResult(metadataResult);
      const query = firstResult(queryResult);
      if (metadata?.Success === false || query?.Success === false) {
        throw new Error(resultMessage(
          query?.Success === false ? query : metadata,
          `${itemName} data is unavailable.`,
        ));
      }
      const nextItems = Array.isArray(query?.Items) ? query.Items : [];
      setItems(nextItems);
      onItems?.(nextItems);
    } catch (ex) {
      setItems([]);
      onItems?.([]);
      setError(ex?.message || `${itemName} data is unavailable.`);
    } finally {
      setLoading(false);
    }
  }, [clientId, itemName, onItems]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !items.length) {
    return <Loading text={`Loading ${itemName}...`} />;
  }

  return (
    <section className="resource-workspace-panel command-resources__grid">
      <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
        <div>
          <h2>{manager?.uiMetadata?.[0]?.DisplayName || itemName}</h2>
          <p>Client and current environment are server locked.</p>
        </div>
        <ConvokeCommandBar
          actions={[{
            key: `refresh-${itemName}`,
            label: 'Refresh',
            iconKey: 'refresh',
            disabled: loading,
            onClick: load,
          }]}
          ariaLabel={`${manager?.uiMetadata?.[0]?.DisplayName || itemName} actions`}
          useSparkButtons
        />
      </div>
      {error ? (
        <ResourceStateControl
          compact
          tone="error"
          title={`${manager?.uiMetadata?.[0]?.DisplayName || itemName} unavailable`}
          message={error}
        />
      ) : null}
      {manager?.uiMetadata ? (
        <TableControl
          data={items}
          defaultNumRows={50}
          disableTableTitle
          enableMultiSelect={false}
          enableSidePanelEdit
          filters={manager.filters}
          loadingState={loading}
          metadata={manager.uiMetadata}
          mgr={manager}
          onServerSideQuery={load}
          customQuickViewList={rowActions}
        />
      ) : null}
    </section>
  );
};

const metric = (label, value) => (
  <div
    className="resource-workspace-metric"
    key={label}
  >
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const ClientResourcesEntitlementsScreen = ({
  screen,
  clientId,
  currentEnvironment,
}) => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState({});
  const collectionHandlers = useMemo(() => Object.fromEntries(
    Object.keys(RESOURCE_ITEMS).map((key) => [
      key,
      (items) => setCollections((current) => (
        current[key] === items ? current : { ...current, [key]: items }
      )),
    ]),
  ), []);
  const offers = collections.offers || [];
  const resources = collections.resources || [];
  const configurations = collections.configurations || [];
  const readiness = collections.readiness || [];
  const capacity = collections.capacity || [];
  const approvalAttention = [...offers, ...resources]
    .filter((item) => [1, 2, 5].includes(Number(item.ApprovalStatus))).length;
  const ready = configurations
    .filter((item) => Number(item.ConfigurationStatus) === 8).length;
  const blockedFailed = configurations
    .filter((item) => Number(item.ConfigurationStatus) === 5).length
    + readiness.filter((item) => item.HasBlockers === true).length;
  const capacityWarnings = capacity.filter((item) => (
    Number(item.Status) === 1
    && item.ExpiresUtc
    && new Date(item.ExpiresUtc).getTime() <= Date.now()
  )).length;
  const permissionWarnings = resources.filter((item) => (
    !Array.isArray(item.ModulePermissionBaseline)
    || item.ModulePermissionBaseline.length === 0
  )).length;

  const resourceActions = useCallback((row) => [{
    name: 'Open Resource Workspace',
    customFunction: () => navigate(buildResourceWorkspacePath(row?._id)),
  }], [navigate]);
  const setupActions = useCallback((row) => [{
    name: 'Open Resource Workspace',
    customFunction: () => navigate(buildResourceWorkspacePath(row?.ClientResourceId)),
  }], [navigate]);

  const grids = {
    offers: (
      <ResourceGrid
        itemName={RESOURCE_ITEMS.offers}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
        onItems={collectionHandlers.offers}
      />
    ),
    resources: (
      <ResourceGrid
        itemName={RESOURCE_ITEMS.resources}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
        onItems={collectionHandlers.resources}
        rowActions={resourceActions}
      />
    ),
    configurations: (
      <ResourceGrid
        itemName={RESOURCE_ITEMS.configurations}
        clientId={clientId}
        currentEnvironment={currentEnvironment}
        onItems={collectionHandlers.configurations}
        rowActions={setupActions}
      />
    ),
    setup: (
      <TabPaneControl
        align="left"
        defaultKey="runs"
        storageKey={`client-command-resource-setup:${clientId}`}
        tabs={[
          {
            key: 'runs',
            title: 'Setup Runs',
            content: (
              <ResourceGrid
                itemName={RESOURCE_ITEMS.setupRuns}
                clientId={clientId}
                currentEnvironment={currentEnvironment}
                onItems={collectionHandlers.setupRuns}
                rowActions={setupActions}
              />
            ),
          },
          {
            key: 'readiness',
            title: 'Readiness',
            content: (
              <ResourceGrid
                itemName={RESOURCE_ITEMS.readiness}
                clientId={clientId}
                currentEnvironment={currentEnvironment}
                onItems={collectionHandlers.readiness}
                rowActions={setupActions}
              />
            ),
          },
        ]}
      />
    ),
    capacity: (
      <TabPaneControl
        align="left"
        defaultKey="leases"
        storageKey={`client-command-resource-capacity:${clientId}`}
        tabs={[
          {
            key: 'leases',
            title: 'Capacity Leases',
            content: (
              <ResourceGrid
                itemName={RESOURCE_ITEMS.capacity}
                clientId={clientId}
                currentEnvironment={currentEnvironment}
                onItems={collectionHandlers.capacity}
              />
            ),
          },
          {
            key: 'usage',
            title: 'Usage Estimates',
            content: (
              <ResourceGrid
                itemName={RESOURCE_ITEMS.usage}
                clientId={clientId}
                currentEnvironment={currentEnvironment}
                onItems={collectionHandlers.usage}
                rowActions={setupActions}
              />
            ),
          },
        ]}
      />
    ),
  };
  const commandBarActions = [
    {
      key: 'open-client-marketplace',
      label: 'Open Marketplace',
      iconKey: 'marketplace',
      disabled: !screen.CanView,
      onClick: () => navigate('/marketplace'),
    },
    {
      key: 'open-client-resource-approvals',
      label: 'Open Resource Approvals',
      iconKey: 'validate',
      disabled: !screen.CanExecute,
      onClick: () => navigate('/admin/resource-approvals/manage'),
    },
    {
      key: 'open-client-resource-workspace',
      label: 'Open Resource Workspace',
      iconKey: 'tools',
      disabled: !screen.CanView,
      onClick: () => navigate(RESOURCES_BASE_PATH),
    },
  ];

  return (
    <div className="resource-workspace-overview command-entry command-resources">
      <ConvokeCommandBar
        actions={commandBarActions}
        ariaLabel="Resources and Entitlements actions"
        className="convoke-command-bar--screen"
        useSparkButtons
      />

      <div className="resource-workspace-metric-grid" aria-label="Resource summary">
        {metric('Product Offers', offers.length)}
        {metric('Client Resources', resources.length)}
        {metric('Pending Approvals', approvalAttention, approvalAttention > 0)}
        {metric('Resource Configurations', configurations.length)}
        {metric('Ready', ready)}
        {metric('Blocked / Failed', blockedFailed, blockedFailed > 0)}
        {metric('Capacity Warnings', capacityWarnings, capacityWarnings > 0)}
        {metric(
          'Permission Baseline Warnings',
          permissionWarnings,
          permissionWarnings > 0,
        )}
      </div>

      <TabPaneControl
        className="command-resources__tabs"
        align="left"
        defaultKey="offers"
        storageKey={`client-command-resources:${clientId}`}
        tabs={[
          { key: 'offers', title: 'Product Offers', content: grids.offers },
          { key: 'resources', title: 'Client Resources', content: grids.resources },
          {
            key: 'configurations',
            title: 'Resource Configurations',
            content: grids.configurations,
          },
          { key: 'setup', title: 'Setup & Readiness', content: grids.setup },
          { key: 'capacity', title: 'Capacity & Usage', content: grids.capacity },
        ]}
      />
    </div>
  );
};

export default ClientResourcesEntitlementsScreen;
export {
  RESOURCE_ITEMS,
  createScopedManager,
};
